'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CurrencyForm, type CurrencyFormRef } from '@/components/partials/currencies'
import { useCreateCurrency } from '@/hooks'
import { CreateCurrencyParams, UpdateCurrencyParams } from '@/types/currency'
import { FormModeEnum } from '@/enums'

const CreateCurrency = () => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const currencyFormRef = useRef<CurrencyFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createCurrency, isPending: isLoading } = useCreateCurrency()

  const handleSubmit = (data: CreateCurrencyParams | UpdateCurrencyParams) => {
    createCurrency(data as CreateCurrencyParams, {
      onSuccess: async newCurrency => {
        setIsRedirecting(true)
        queryClient.setQueryData(['currency', newCurrency.id], newCurrency)
        await queryClient.invalidateQueries({ queryKey: ['currencies'] })
        toast.success(t('created_successfully'))
        router.push(`/currencies/${newCurrency.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateCurrencyParams | UpdateCurrencyParams) => {
    createCurrency(data as CreateCurrencyParams, {
      onSuccess: async newCurrency => {
        queryClient.setQueryData(['currency', newCurrency.id], newCurrency)
        await queryClient.invalidateQueries({ queryKey: ['currencies'] })
        const detailUrl = `/currencies/${newCurrency.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        currencyFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (currencyFormRef.current) {
        currencyFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating_currency'))
  }

  const handleCancel = () => {
    router.push('/currencies')
  }

  return (
    <CurrencyForm
      key={formKey}
      ref={currencyFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default CreateCurrency
